import { Package, RegistrationAffiliation } from "@/api/dtos";
import { AdminContext } from "@/contexts/AdminContext";
import IsNextYearEarlyRegistrationContext from "@/contexts/IsNextYearEarlyRegistrationContext";
import PackageContext, { IPackageContext } from "@/contexts/PackageContext";
import useAdditionalSessions from "@/hooks/useAdditionalSessions";
import useAvailablePackages from "@/hooks/useAvailablePackages";
import useGetVoucherIdByCode from "@/hooks/useGetVoucherIdByCode";
import useVoucherPreselectedPackage from "@/hooks/useGetVoucherPreselectedPackage";
import { useHasTAndECSessionAccess } from "@/hooks/useUserRoles";
import { useScopedField } from "@ngt/forms-core";
import { FC, PropsWithChildren, useContext, useMemo } from "react";

const useFormPackages = (isAdmin: boolean) => {
    const {state: {value: enteredDate}} = useScopedField<string>("enteredDate", {value: true});
    const {state: {value: member}} = useScopedField<number>("member", {value: true});
    const {state: {value: trialCoordinator}} = useScopedField<boolean>("trialCoordinator", {value: true});
    const {state: {value: student}} = useScopedField<boolean>("student", {value: true});
    const {state: {value: registrar}} = useScopedField<boolean>("registrar", {value: true});
    const {state: {value: staff}} = useScopedField<boolean>("staff", {value: true});
    const {state: {value: attendeeFromJapanOrKorea}} = useScopedField<boolean>("attendeeFromJapanOrKorea", {value: true});
    const {state: {value: isSac}} = useScopedField<boolean>("isSac", {value: true});
    const {state: {value: sponsor}} = useScopedField<boolean>("sponsor", {value: true});
    const {state: {value: freeSponsorRegistration}} = useScopedField<boolean>("freeSponsorRegistration", {value: true});

    const {state: {value: discountCode}} = useScopedField<string>("discountCode", {value: true});
    const {state: {value: voucherId}} = useScopedField<number>("voucherId", {value: true});
    const codeForLookup = isAdmin ? undefined : discountCode; // use only the voucherId in the registration.
    const { data: voucherLookupResponse } = useGetVoucherIdByCode(codeForLookup ?? undefined);

    
    const {state: { value: registrationAffiliation }} = useScopedField<number>("registrationAffiliation", { value: true });
    // This is distinct from the current-year early-bird discount cutoff.
    const isNextYearEarlyRegistration = useContext(IsNextYearEarlyRegistrationContext);
    const isCap = registrationAffiliation === RegistrationAffiliation.CAP;
    const isSpeaker = registrationAffiliation != null
        && registrationAffiliation !== RegistrationAffiliation.TraineeAndEarlyCareersDayGrant;

    const voucher = useMemo(() => {
        if (codeForLookup) {
            return voucherLookupResponse?.voucher?.id;
        }

        return voucherId ?? undefined;
    }, [codeForLookup, voucherLookupResponse?.voucher?.id, voucherId]);

    return useAvailablePackages(member ?? undefined,  
        trialCoordinator ?? false, 
        student ?? undefined, 
        registrar ?? undefined, 
        staff ?? undefined, 
        attendeeFromJapanOrKorea ?? undefined, 
        sponsor ?? undefined, 
        freeSponsorRegistration ?? undefined, 
        isSac ?? undefined, 
        isCap ?? undefined, 
        isSpeaker ?? undefined,
        isNextYearEarlyRegistration ?? undefined,
        voucher,
        enteredDate ?? undefined
    ); 
}

const usePackage = (packages?: Package[]): Package | undefined => {
    const {state: { value: packageId }} = useScopedField<number>("packageId", { value: true }, undefined, false);

    return useMemo(() => {
        if (!packageId || !packages) {
            return undefined;
        }

        return packages.find(x => x.id === packageId);
    }, [packageId, packages])
}

const PackageProvider: FC<PropsWithChildren> = ({
    children
}) => {
    const isAdmin = useContext(AdminContext);
    const {state: { value: registrationAffiliation }} = useScopedField<number>("registrationAffiliation", { value: true }, undefined, false);
    const hasTAndECSessionAccess = useHasTAndECSessionAccess();
    
    const hasSpeakerPrivileges = useMemo(() => {
        return registrationAffiliation != null
            && registrationAffiliation !== RegistrationAffiliation.TraineeAndEarlyCareersDayGrant;
    }, [registrationAffiliation]);

    const {state: {value: discountCode}} = useScopedField<string>("discountCode", {value: true});
    const {state: {value: voucherId}} = useScopedField<number>("voucherId", {value: true});
    const {state: {value: packageId}} = useScopedField<number>("packageId", {value: true});
    const { packages, loading } = useFormPackages(isAdmin);
    const packageInformation = usePackage(packages);

    const codeForAdditionalSessions = isAdmin && packageId ? undefined : (isAdmin ? discountCode : discountCode);
    const additionalSessionsRequestId = packageInformation?.id ?? (codeForAdditionalSessions ? 0 : undefined);
    const { additionalSessions, loading: additionalSessionsLoading } = useAdditionalSessions(additionalSessionsRequestId, isAdmin, hasSpeakerPrivileges ?? false, hasTAndECSessionAccess, codeForAdditionalSessions ?? undefined, voucherId ?? undefined);
    const { data: voucherPreselectedPackage, loading: voucherPreselectedPackageLoading } = useVoucherPreselectedPackage(codeForAdditionalSessions ?? undefined);

    const filteredAdditionalSessions = useMemo(() => {
        // The API already applies the authoritative voucher/session filtering.
        // This client-side allowlist only exists as a guard against stale or out-of-order
        // UI fetches when a voucher preselects a package before the additional-session
        // request has settled. Limit it to explicit ShowThisPackageOnly vouchers so we do
        // not change the behaviour of ordinary discount vouchers.
        if (!codeForAdditionalSessions) {
            return additionalSessions;
        }

        if (!additionalSessions) {
            return additionalSessions;
        }

        if (voucherPreselectedPackage?.restrictAdditionalSessionsToVoucherSessions !== true) {
            return additionalSessions;
        }

        const preselectedPackageSessionIds = voucherPreselectedPackage?.preselectedPackageSessionIds;

        if (!preselectedPackageSessionIds || preselectedPackageSessionIds.length === 0) {
            return additionalSessions;
        }

        // GetVoucherPreselectedPackage returns session ids, not PackageSession ids, so the
        // UI can compare them directly with the Session records from useAdditionalSessions.
        const allowedSessionIds = new Set(preselectedPackageSessionIds);

        return additionalSessions.filter(session => session.id != null && allowedSessionIds.has(session.id));
    }, [
        additionalSessions,
        codeForAdditionalSessions,
        voucherPreselectedPackage?.preselectedPackageSessionIds,
        voucherPreselectedPackage?.restrictAdditionalSessionsToVoucherSessions
    ]);

    const context = useMemo(() => {
        const c: IPackageContext = {
            packages: packages,
            packagesLoading: loading,
            package: packageInformation,
            additionalSessions: filteredAdditionalSessions,
            additionalSessionsLoading: additionalSessionsLoading || voucherPreselectedPackageLoading
        }

        return c;
    }, [packages, loading, packageInformation, filteredAdditionalSessions, additionalSessionsLoading, voucherPreselectedPackageLoading]);

        return (
            <PackageContext.Provider value={context}>
                {children}
            </PackageContext.Provider>
        )
    }

export default PackageProvider;
